import React, { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import useLanguage from "../../utils/useLanguage";

const ContainerBalancePrint = forwardRef(
  ({ selectedRow, selectedSupplier, currentDirection = "ltr" }, ref) => {
    const formatDate = (date) => {
      if (!date) return t("No date available") || "No date available";
      return new Date(date).toISOString().split("T")[0];
    };

    const toMoney = (amount) => {
      if (amount === null || amount === undefined) return "0.00";
      return Number(amount).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    };

    const { t } = useTranslation("general");

    const getAmountColor = () => {
      return selectedRow?.balanceAfterUpdate < selectedRow?.balanceBeforeUpdate
        ? "#e74c3c"
        : "#27ae60";
    };

    const getBalanceColor = () => {
      return selectedRow?.balanceAfterUpdate >= 0 ? "#27ae60" : "#e74c3c";
    };

    return (
      <div ref={ref} className={`print-invoice`} dir={currentDirection}>
        {/* Header */}
        <div className="print-header">
          <div className="company-info">
            <h1 className="company-name">{t("containerBalanceReport")}</h1>
          </div>
          <div className="invoice-meta">
            <p className="print-date">
              {t("printDate") || "Print Date"}:{" "}
              {new Date().toLocaleDateString()}
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="print-content">
          {/* Supplier Section */}
          <div className="info-section">
            <h2 className="section-title">{t("supplier") || "Supplier"}</h2>
            <div className="info-card">
              <div className="info-row">
                <span className="info-label">
                  {t("supplierName") || "Name"}:
                </span>
                <span className="info-value supplier-name">
                  {selectedSupplier?.name ||
                    t("No supplier available") ||
                    "No supplier available"}
                </span>
              </div>
              {selectedSupplier?.currency && (
                <div className="info-row">
                  <span className="info-label">
                    {t("currency") || "Currency"}:
                  </span>
                  <span className="info-value">
                    {selectedSupplier.currency}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Balance Information */}
          <div className="info-section">
            <h2 className="section-title">
              {t("balanceDetails") || "Balance Details"}
            </h2>
            <div className="balance-grid">
              <div className="balance-item">
                <div className="balance-label">
                  {t("balanceBeforeUpdate") || "Balance Before Update"}
                </div>
                <div className="balance-value">
                  {toMoney(selectedRow?.balanceBeforeUpdate)}
                  {selectedSupplier?.currency && (
                    <span className="currency">
                      {" "}
                      {selectedSupplier.currency}
                    </span>
                  )}
                </div>
              </div>

              <div className="balance-item highlight">
                <div className="balance-label">
                  {t("containersPrices") || "Container Prices"}
                </div>
                <div
                  className="balance-value"
                  style={{ color: getAmountColor() }}
                >
                  {toMoney(selectedRow?.amountInSupCur)}
                  {selectedSupplier?.currency && (
                    <span className="currency">
                      {" "}
                      {selectedSupplier.currency}
                    </span>
                  )}
                </div>
              </div>

              <div className="balance-item">
                <div className="balance-label">
                  {t("balanceAfterUpdate") || "Balance After Update"}
                </div>
                <div
                  className="balance-value"
                  style={{ color: getBalanceColor() }}
                >
                  {toMoney(selectedRow?.balanceAfterUpdate)}
                  {selectedSupplier?.currency && (
                    <span className="currency">
                      {" "}
                      {selectedSupplier.currency}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Transaction Details */}
          <div className="info-section">
            <h2 className="section-title">
              {t("transactionDetails") || "Transaction Details"}
            </h2>
            <div className="info-card">
              <div className="info-row">
                <span className="info-label">{t("Date") || "Date"}:</span>
                <span className="info-value">
                  {formatDate(selectedRow?.modifiedAt || selectedRow?.date)}
                </span>
              </div>

              {selectedRow?.theDateOfUpdate && (
                <div className="info-row">
                  <span className="info-label">
                    {t("lastUpdated") || "Last Updated"}:
                  </span>
                  <span className="info-value">
                    {formatDate(selectedRow.theDateOfUpdate)}
                  </span>
                </div>
              )}

              <div className="info-row notes-row">
                <span className="info-label">{t("notes") || "Notes"}:</span>
                <div className="notes-content">
                  {selectedRow?.notes ||
                    t("No notes available") ||
                    "No notes available"}
                </div>
              </div>
            </div>
          </div>

          {/* Summary Box */}
          <div className="summary-box">
            <h3>{t("transactionSummary") || "Transaction Summary"}</h3>
            <div className="summary-content">
              <p>
                {t("balanceChange") || "Balance Change"}:
                <span
                  className="balance-change"
                  style={{ color: getAmountColor() }}
                >
                  {selectedRow?.balanceAfterUpdate >
                  selectedRow?.balanceBeforeUpdate
                    ? "+"
                    : ""}
                  {toMoney(
                    (selectedRow?.balanceAfterUpdate || 0) -
                      (selectedRow?.balanceBeforeUpdate || 0)
                  )}
                  {selectedSupplier?.currency &&
                    ` ${selectedSupplier.currency}`}
                </span>
              </p>
            </div>
          </div>
        </div>

        <div className="footer">
          <p className="t-headline-3">{t("ehsiblySecondSlogan")}</p>
          <p>{t("alhabbarSlogan")}</p>
        </div>
      </div>
    );
  }
);

export default ContainerBalancePrint;
